import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { users, auth } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UserCog, Shield, Users as UsersIcon, Plus, Trash2, Key, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Amministratore", description: "Accesso completo a tutte le funzionalità", color: "bg-red-100 text-red-700 border-red-200" },
  { value: "operator", label: "Operatore", description: "Può creare e modificare dati, ma non eliminare", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "user", label: "Utente", description: "Solo visualizzazione dati", color: "bg-slate-100 text-slate-700 border-slate-200" },
];

const initialFormData = {
  email: "",
  password: "",
  name: "",
  role: "user"
};

export default function GestioneUtenti() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [showPassword, setShowPassword] = useState(false);

  const queryClient = useQueryClient();

  const { data: usersData, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: users.list,
  });

  const createUserMutation = useMutation({
    mutationFn: auth.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setCreateDialogOpen(false);
      setFormData(initialFormData);
      toast.success("Utente creato con successo");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }) => users.updateRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success("Ruolo aggiornato");
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteUserMutation = useMutation({
    mutationFn: auth.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success("Utente eliminato");
    },
    onError: (error) => toast.error(error.message),
  });

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    createUserMutation.mutate(formData);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getRoleConfig = (role) => ROLES.find(r => r.value === role) || ROLES[2];

  // Error state - not admin
  if (error) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="gestione-utenti-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Gestione Utenti</h1>
          <p className="text-slate-500 mt-1">Amministrazione utenti e ruoli</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8 text-slate-500">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Accesso non autorizzato</p>
              <p className="text-sm mt-2">Solo gli amministratori possono accedere a questa sezione</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gestione-utenti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Gestione Utenti</h1>
          <p className="text-slate-500 mt-1">Crea e gestisci gli utenti del sistema</p>
        </div>
        
        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="create-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Crea Nuovo Utente</DialogTitle>
              <DialogDescription>
                Inserisci i dati per creare un nuovo utente nel sistema.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mario Rossi"
                  required
                  data-testid="input-user-name"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mario.rossi@email.com"
                  required
                  data-testid="input-user-email"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimo 8 caratteri"
                    minLength={8}
                    required
                    data-testid="input-user-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Ruolo *</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="select-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-slate-500">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setCreateDialogOpen(false);
                    setFormData(initialFormData);
                  }}
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#0B1E3D] hover:bg-[#1E40AF]"
                  disabled={createUserMutation.isPending}
                  data-testid="submit-create-user"
                >
                  {createUserMutation.isPending ? "Creazione..." : "Crea Utente"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <UsersIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{usersData?.length || 0}</p>
                <p className="text-xs text-slate-500">Utenti Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <Shield className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{usersData?.filter(u => u.role === "admin").length || 0}</p>
                <p className="text-xs text-slate-500">Amministratori</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <UserCog className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{usersData?.filter(u => u.role === "operator").length || 0}</p>
                <p className="text-xs text-slate-500">Operatori</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-slate-700">Legenda Ruoli</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ROLES.map(role => (
              <div key={role.value} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <Badge className={role.color}>{role.label}</Badge>
                <p className="text-xs text-slate-600">{role.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Utenti</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1E3D]"></div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="users-list">
              {usersData?.map(user => {
                const roleConfig = getRoleConfig(user.role);
                return (
                  <div 
                    key={user.user_id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-lg"
                    data-testid={`user-item-${user.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback className="bg-[#0B1E3D] text-white text-sm">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{user.name || "Utente"}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                        {user.created_at && (
                          <p className="text-xs text-slate-400">
                            Creato: {new Date(user.created_at).toLocaleDateString('it-IT')}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Role Selector */}
                      <Select
                        value={user.role || "user"}
                        onValueChange={(role) => updateRoleMutation.mutate({ userId: user.user_id, role })}
                      >
                        <SelectTrigger className="w-44" data-testid={`role-select-${user.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(role => (
                            <SelectItem key={role.value} value={role.value}>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  role.value === 'admin' ? 'bg-red-500' : 
                                  role.value === 'operator' ? 'bg-blue-500' : 'bg-slate-400'
                                }`}></span>
                                {role.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Current Role Badge */}
                      <Badge className={`hidden sm:flex ${roleConfig.color}`}>
                        {roleConfig.label}
                      </Badge>
                      
                      {/* Delete Button */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare questo utente?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Stai per eliminare l'utente <strong>{user.name}</strong> ({user.email}).
                              Questa azione non può essere annullata.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteUserMutation.mutate(user.user_id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                );
              })}
              
              {(!usersData || usersData.length === 0) && (
                <div className="text-center py-8 text-slate-500">
                  <UsersIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun utente registrato</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Key className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Informazioni sui permessi</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• <strong>Amministratore:</strong> Può creare/eliminare utenti, accedere a tutti i dati sensibili, gestire GDPR</li>
                <li>• <strong>Operatore:</strong> Può creare/modificare persone, mezzi, turni. Non può eliminare né gestire utenti</li>
                <li>• <strong>Utente:</strong> Può solo visualizzare i dati e usare gli scanner badge</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
